import { Component, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import type { ApiCity, ApiOrderStatusHistoryEntry, OrderStatus, SearchOrderRow } from '../../core/api/api.types';

const OPERATOR_RELEVANT_STATUSES = new Set<OrderStatus>(['confirmed', 'in_progress', 'completed', 'completed_externally']);
import { ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS, PAYMENT_TYPE_LABELS, SERVICE_MODEL_LABELS } from '../../core/api/api.types';
import { HandoffApiService } from '../../core/api/handoff-api.service';
import { OrdersApiService } from '../../core/api/orders-api.service';
import { ReferenceApiService } from '../../core/api/reference-api.service';

const PAGE_STATUSES: OrderStatus[] = ['confirmed', 'in_progress', 'completed', 'completed_externally', 'referral_sent'];

type SortField = 'status' | 'updated_at' | 'city_code';
type SortDir = 'asc' | 'desc';

@Component({
  selector: 'op-confirmed-completed-page',
  imports: [
    DatePipe,
    FormsModule,
    RouterModule,
    MatButtonToggleModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './confirmed-completed.page.html',
  styleUrl: './confirmed-completed.page.scss',
})
export class ConfirmedCompletedPage implements OnInit {
  private readonly ordersApi   = inject(OrdersApiService);
  private readonly refApi      = inject(ReferenceApiService);
  private readonly handoffApi  = inject(HandoffApiService);
  private readonly router      = inject(Router);

  // ── Server state ────────────────────────────────────────────────────────────
  readonly orders  = signal<SearchOrderRow[]>([]);
  readonly loading = signal(false);
  readonly error   = signal<string | null>(null);
  readonly cities  = signal<ApiCity[]>([]);

  // ── Selected order / detail panel ───────────────────────────────────────────
  readonly selectedOrder = signal<SearchOrderRow | null>(null);

  // ── Timeline modal ──────────────────────────────────────────────────────────
  readonly timelineOrder   = signal<SearchOrderRow | null>(null);
  readonly timeline        = signal<ApiOrderStatusHistoryEntry[]>([]);
  readonly timelineLoading = signal(false);

  // ── Confirm close modal ─────────────────────────────────────────────────────
  readonly confirmingClose = signal<SearchOrderRow | null>(null);
  readonly closing         = signal(false);
  readonly closeError      = signal<string | null>(null);
  readonly closingOrderId  = signal<string | null>(null);

  // ── Detail: operator history (lazy loaded when panel opens) ─────────────────
  readonly detailHistory        = signal<ApiOrderStatusHistoryEntry[]>([]);
  readonly detailHistoryLoading = signal(false);

  /** Last operator who moved this order into a confirmed/completed state. */
  readonly lastRelevantOperator = computed(() => {
    const entries = this.detailHistory();
    for (let i = entries.length - 1; i >= 0; i--) {
      const e = entries[i];
      if (e.changed_by_admin_id && OPERATOR_RELEVANT_STATUSES.has(e.to_status as OrderStatus)) {
        return e;
      }
    }
    return null;
  });

  // ── Filters ─────────────────────────────────────────────────────────────────
  searchQuery      = '';
  selectedStatuses: OrderStatus[] = [...PAGE_STATUSES];
  selectedCityCodes: string[]     = [];
  providerQuery    = '';
  cityDropdownOpen = false;
  sortField: SortField = 'updated_at';
  sortDir: SortDir     = 'desc';

  // ── Client-side provider filter ──────────────────────────────────────────────
  readonly filteredOrders = computed(() => {
    const q = this.providerQuery.trim().toLowerCase();
    if (!q) return this.orders();
    return this.orders().filter((o) => {
      const name = (o.providers?.name ?? '').toLowerCase();
      return name.includes(q);
    });
  });

  // ── Exposed constants ────────────────────────────────────────────────────────
  readonly pageStatuses = PAGE_STATUSES;

  async ngOnInit(): Promise<void> {
    const [, citiesRes] = await Promise.all([
      this.loadOrders(),
      this.refApi.cities().catch(() => ({ items: [] as ApiCity[] })),
    ]);
    this.cities.set(citiesRes.items);
  }

  // ── Filter actions ───────────────────────────────────────────────────────────

  async onSearch(): Promise<void> {
    await this.loadOrders();
  }

  onStatusChange(values: OrderStatus[]): void {
    this.selectedStatuses = values.length ? values : [...PAGE_STATUSES];
    void this.loadOrders();
  }

  toggleCity(code: string): void {
    const idx = this.selectedCityCodes.indexOf(code);
    if (idx === -1) {
      this.selectedCityCodes = [...this.selectedCityCodes, code];
    } else {
      this.selectedCityCodes = this.selectedCityCodes.filter((c) => c !== code);
    }
    void this.loadOrders();
  }

  async toggleSort(field: SortField): Promise<void> {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir   = field === 'updated_at' ? 'desc' : 'asc';
    }
    await this.loadOrders();
  }

  // ── Detail panel ─────────────────────────────────────────────────────────────

  openDetail(order: SearchOrderRow): void {
    this.selectedOrder.set(order);
    void this.loadDetailHistory(order.id);
  }

  closeDetail(): void {
    this.selectedOrder.set(null);
    this.detailHistory.set([]);
  }

  private async loadDetailHistory(orderId: string): Promise<void> {
    this.detailHistory.set([]);
    this.detailHistoryLoading.set(true);
    try {
      const res = await this.ordersApi.listStatusHistory(orderId);
      if (this.selectedOrder()?.id === orderId) {
        this.detailHistory.set(res.items);
      }
    } catch {
      // non-fatal — section will show loading state
    } finally {
      this.detailHistoryLoading.set(false);
    }
  }

  // ── Timeline modal ───────────────────────────────────────────────────────────

  async openTimeline(order: SearchOrderRow): Promise<void> {
    this.timelineOrder.set(order);
    this.timeline.set([]);
    this.timelineLoading.set(true);
    try {
      const res = await this.ordersApi.listStatusHistory(order.id);
      this.timeline.set(res.items);
    } catch {
      this.timeline.set([]);
    } finally {
      this.timelineLoading.set(false);
    }
  }

  closeTimeline(): void {
    this.timelineOrder.set(null);
  }

  // ── Chat initiation ──────────────────────────────────────────────────────────

  readonly chatInitiating = signal(false);
  readonly chatInitiateError = signal<string | null>(null);

  async initiateChatWithCustomer(order: SearchOrderRow): Promise<void> {
    if (this.chatInitiating()) return;
    this.chatInitiating.set(true);
    this.chatInitiateError.set(null);
    try {
      const { handoffId } = await this.handoffApi.operatorInitiate(order.id);
      void this.router.navigate(['/my-cases', handoffId]);
    } catch (err: any) {
      this.chatInitiateError.set(err?.message ?? 'Failed to open chat.');
    } finally {
      this.chatInitiating.set(false);
    }
  }

  // ── Close order ──────────────────────────────────────────────────────────────

  requestClose(order: SearchOrderRow): void {
    this.closeError.set(null);
    this.confirmingClose.set(order);
  }

  cancelClose(): void {
    this.confirmingClose.set(null);
  }

  async confirmClose(): Promise<void> {
    const order = this.confirmingClose();
    if (!order) return;
    this.closing.set(true);
    this.closeError.set(null);
    try {
      await this.ordersApi.updateOrderStatus(order.id, 'closed', 'Closed by operator');
      this.confirmingClose.set(null);
      if (this.selectedOrder()?.id === order.id) this.selectedOrder.set(null);
      this.closingOrderId.set(order.id);
      setTimeout(() => {
        this.closingOrderId.set(null);
        void this.loadOrders();
      }, 320);
    } catch (err: any) {
      this.closeError.set(err?.message ?? 'Failed to close order.');
    } finally {
      this.closing.set(false);
    }
  }

  // ── Display helpers ──────────────────────────────────────────────────────────

  statusLabel(status: string): string {
    return (ORDER_STATUS_LABELS as Record<string, string>)[status] ?? status;
  }

  statusTone(status: string): 'blue' | 'orange' | 'red' | 'green' {
    switch (status) {
      case 'finding_provider':
      case 'in_progress':          return 'blue';
      case 'confirmed':
      case 'completed':
      case 'completed_externally':
      case 'referral_sent':        return 'green';
      case 'closed':
      case 'cancelled':            return 'red';
      default:                     return 'orange';
    }
  }

  paymentStatusTone(status: string | null | undefined): 'green' | 'orange' | 'red' | 'default' {
    switch (status) {
      case 'paid':      return 'green';
      case 'awaiting':
      case 'pending':   return 'orange';
      case 'failed':    return 'red';
      default:          return 'default';
    }
  }

  providerLabel(order: SearchOrderRow): string {
    if (order.providers?.name) return order.providers.name;
    if (order.provider_assignment_type === 'unknown') return 'Unknown';
    if (order.provider_assignment_type === 'other_in_notes') return 'Other (in notes)';
    return '—';
  }

  customerName(order: SearchOrderRow): string {
    return order.handoff?.customer_preferred_name || '—';
  }

  serviceModelLabel(model: string): string {
    return (SERVICE_MODEL_LABELS as Record<string, string>)[model] ?? model;
  }

  paymentTypeLabel(type: string | null | undefined): string {
    if (!type) return '—';
    return (PAYMENT_TYPE_LABELS as Record<string, string>)[type] ?? type;
  }

  paymentStatusLabel(status: string | null | undefined): string {
    if (!status) return '—';
    return (PAYMENT_STATUS_LABELS as Record<string, string>)[status] ?? status;
  }

  centsToDollars(cents: number | null | undefined): string {
    if (!cents) return '—';
    return `$${(cents / 100).toFixed(2)}`;
  }

  historyActor(entry: ApiOrderStatusHistoryEntry): string {
    if (!entry.changed_by_admin_id) return 'System';
    return entry.changed_by_display_name || 'Operator';
  }

  cityLabel(code: string): string {
    return this.cities().find((c) => c.code === code)?.label ?? code;
  }

  isCitySelected(code: string): boolean {
    return this.selectedCityCodes.includes(code);
  }

  // ── Dropdown outside-click ───────────────────────────────────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.cityDropdownOpen) return;
    if (!(event.target as HTMLElement).closest('.city-filter')) {
      this.cityDropdownOpen = false;
    }
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  private async loadOrders(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const statuses = this.selectedStatuses.length ? this.selectedStatuses : PAGE_STATUSES;
      const res = await this.ordersApi.searchOrders({
        search:    this.searchQuery || undefined,
        statuses,
        cityCodes: this.selectedCityCodes.length ? this.selectedCityCodes : undefined,
        sortBy:    this.sortField,
        sortDir:   this.sortDir,
      });
      this.orders.set(res.items);
      // keep selected order in sync
      const sel = this.selectedOrder();
      if (sel) {
        const updated = res.items.find((o) => o.id === sel.id);
        this.selectedOrder.set(updated ?? null);
      }
    } catch (err: any) {
      this.error.set(err?.message ?? 'Failed to load orders.');
    } finally {
      this.loading.set(false);
    }
  }
}
